import { useState } from "react";
import { getToolTrelica, markReviewed } from "@/api/endpoints";
import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/Button";
import type { Curation } from "@/api/types";

// What needs a person's attention on one tool or practice (backend domain/flags.py), with the
// two fixes that can happen right here — marking it reviewed and, for a tool, re-reading its
// Trelica app — plus its usage evidence. Everything else is fixed at the source, linked from
// the sections below. This detail page is the only place an entry can be marked reviewed.

const HEADING = "mb-1 text-xs font-semibold tracking-wide text-text/60 uppercase";

/** The entry's flags and usage. Shows nothing but the usage line when there are no flags.
 * `trelicaAppId` (tools only) offers Re-sync from Trelica on a Trelica flag. `onChanged`
 * reloads the entry after an action. */
export function FlagsSection({
  type,
  entry: t,
  trelicaAppId,
  onChanged,
}: {
  type: "tool" | "practice";
  entry: Curation & { id: string };
  trelicaAppId?: string | null;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Runs one action, then reloads the tool so its flags are recomputed. */
  async function run(fn: () => Promise<unknown>) {
    setError(null);
    setBusy(true);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.errors.join("; ") : String(e));
    } finally {
      setBusy(false);
    }
  }

  const unreviewed = t.flags.some((f) => f.kind === "unreviewed");
  const trelicaFlag = trelicaAppId && t.flags.some((f) => f.kind === "broken_link" || f.kind === "radar_trelica");
  const { suggested, accepted } = t.usage;

  return (
    <section className="flex flex-col gap-2">
      {t.flags.length > 0 && (
        <div className="rounded border border-warn-line bg-warn-bg p-2.5">
          <h4 className={HEADING}>Needs attention</h4>
          <ul className="flex flex-col gap-1 text-sm">
            {t.flags.map((f, i) => (
              <li key={i} className="text-warn">
                {f.message}
              </li>
            ))}
          </ul>
          {(unreviewed || trelicaFlag) && (
            <div className="mt-2 flex flex-wrap gap-2">
              {unreviewed && (
                <Button onClick={() => run(() => markReviewed(type, t.id))} isDisabled={busy}>
                  Mark reviewed
                </Button>
              )}
              {trelicaFlag && (
                <Button onClick={() => run(() => getToolTrelica(t.id))} isDisabled={busy}>
                  Re-sync from Trelica
                </Button>
              )}
            </div>
          )}
          {error && <p className="mt-1 text-sm text-warn">{error}</p>}
        </div>
      )}
      <p className="text-xs text-text/50">
        {suggested === 0
          ? "Not suggested in any map yet."
          : `Suggested in ${suggested} map${suggested === 1 ? "" : "s"}; accepted in ${accepted} signed-off map${accepted === 1 ? "" : "s"}.`}
        {t.reviewed_at && ` Reviewed by ${t.reviewed_by} on ${new Date(t.reviewed_at).toLocaleDateString()}.`}
      </p>
    </section>
  );
}
